// The site card shown in the sheet when a pin is tapped.
import { h, icon } from './dom.js';
import { ICONS } from './icons.js';
import { formatDistance, walkMinutes } from '../lib/geo.js';
import { streetViewUrl } from '../lib/links.js';
import { fallbackWidth, coverPositionY } from '../lib/photos.js';

export const PRIVATE_NOTICE = 'Private residence, please view from the sidewalk.';
const WIDTHS = [480, 1200, 2000];
const HERO_SIZES = '(min-width: 900px) 380px, 100vw';

/** <picture> for a processed photo (pipeline names: <file>-<width>.<ext>; JPEG only at the fallback width). */
function picture(site, photo, { sizes = HERO_SIZES, alt = photo.alt } = {}) {
  const base = `/photos/${site.slug}/${photo.file}`;
  const widths = photo.widths?.length ? photo.widths : WIDTHS;
  const set = (ext) => widths.map((w) => `${base}-${w}.${ext} ${w}w`).join(', ');
  return h('picture', {},
    h('source', { type: 'image/avif', srcset: set('avif'), sizes }),
    h('source', { type: 'image/webp', srcset: set('webp'), sizes }),
    h('img', { src: `${base}-${fallbackWidth(widths)}.jpg`, alt, loading: 'lazy', decoding: 'async' }));
}

/** "Photo: HistoryHiker · CC BY 4.0"; drawings carry their own credit only. */
const creditLine = (p) => (p.kind === 'illustration' ? p.credit : [p.credit, p.license].filter(Boolean).join(' · '));

function hero(site) {
  const photos = site.photos ?? [];
  const photo = photos.find((p) => p.kind === 'hero');
  const drawing = photos.find((p) => p.kind === 'illustration');
  const extras = photos.filter((p) => p.kind === 'detail' || p.kind === 'plaque');
  if (!photo && !drawing) {
    return h('div', { class: 'hero hero-empty' }, icon(ICONS.camera), h('span', {}, 'Picture coming soon'));
  }
  const first = photo ?? drawing;
  const frame = h('div', { class: 'hero' });
  const credit = h('p', { class: 'hero-credit' });
  const show = (p) => {
    frame.replaceChildren(picture(site, p));
    // Centre the chosen focus line once the image size is known.
    const img = frame.querySelector('img');
    const place = () => {
      if (!img.naturalWidth || !frame.clientWidth) return;
      const y = coverPositionY(p.focus_y ?? 50, img.naturalHeight / img.naturalWidth, frame.clientWidth, frame.clientHeight);
      frame.style.setProperty('--focus-y', `${y}%`);
    };
    frame.style.setProperty('--focus-y', '50%');
    if (img.complete) place(); else img.addEventListener('load', place, { once: true });
    frame.classList.toggle('hero-drawing', p.kind === 'illustration');
    credit.textContent = creditLine(p);
    for (const b of controls.querySelectorAll('[data-file]')) b.setAttribute('aria-pressed', String(b.dataset.file === p.file));
  };
  const controls = h('div', { class: 'hero-controls' });

  // Photo and drawing: a two-way toggle (the photo first).
  if (photo && drawing) {
    controls.append(h('div', { class: 'seg', role: 'group', 'aria-label': 'Show photo or drawing' },
      h('button', { type: 'button', dataset: { file: photo.file }, onclick: () => show(photo) }, 'Photo'),
      h('button', { type: 'button', dataset: { file: drawing.file }, onclick: () => show(drawing) }, 'Drawing')));
  }
  // Detail and plaque photos: a strip of thumbnails that swap into the frame.
  if (photo && extras.length) {
    controls.append(h('div', { class: 'strip', role: 'group', 'aria-label': 'More photos' },
      [photo, ...extras].map((p) => h('button', {
        type: 'button', class: 'strip-thumb', dataset: { file: p.file }, 'aria-label': `Show photo: ${p.alt}`, onclick: () => show(p),
      }, picture(site, p, { sizes: '48px', alt: '' })))));
  }
  show(first);
  return h('div', {}, frame, h('div', { class: 'hero-bar' }, credit, controls));
}

/**
 * site: properties + lat/lon. ctx: { category, distance, visited, onBack, onDirections(mode),
 *   onToggleVisited, onChangeMapsApp }
 */
export function renderCard(site, ctx) {
  const checked = site.fact_check_status !== 'unreviewed';
  const meta = [site.year_built && `Year: ${site.year_built}`, site.town,
    ctx.distance != null && `${formatDistance(ctx.distance)} away`].filter(Boolean).join(' · ');

  const sources = site.sources ?? [];
  const sourcesList = h('ul', { class: 'sources', hidden: true },
    sources.map((s) => h('li', {}, h('a', { href: s.url, target: '_blank', rel: 'noopener noreferrer' }, s.title, icon(ICONS.external)))));
  const story = checked && site.story
    ? h('div', { class: 'story', hidden: true }, site.story.split(/\n{2,}/).map((p) => h('p', {}, p)))
    : null;

  const action = (label, svg, onclick, attrs = {}) => h(attrs.href ? 'a' : 'button',
    { class: 'action', type: attrs.href ? null : 'button', onclick, ...attrs }, icon(svg), h('span', {}, label));

  const walkNote = ctx.distance != null
    ? h('p', { class: 'estimate' }, `About ${walkMinutes(ctx.distance)} min on foot (straight-line estimate)`)
    : null;

  const card = h('article', { class: 'card', 'aria-labelledby': 'card-title' },
    h('div', { class: 'card-top' },
      h('button', { type: 'button', class: 'back', onclick: ctx.onBack, 'aria-label': 'Back to nearby sites' }, icon(ICONS.back), 'Nearby'),
      h('button', {
        type: 'button', class: `visited-toggle${ctx.visited ? ' on' : ''}`, 'aria-pressed': String(!!ctx.visited), onclick: ctx.onToggleVisited,
      }, icon(ICONS.check), ctx.visited ? 'Visited' : 'Mark visited')),
    hero(site),
    h('div', { class: 'card-head' },
      ctx.category && h('span', { class: 'chip', style: { '--chip': ctx.category.color } }, ctx.category.label),
      h('h2', { id: 'card-title', class: 'card-title' }, site.name),
      h('p', { class: 'address' }, site.address),
      meta && h('p', { class: 'meta' }, meta),
      checked && site.original_use && h('p', { class: 'meta' }, `Originally: ${site.original_use}`)),
    site.is_private_residence
      ? h('p', { class: 'notice' }, icon(ICONS.home), h('span', {}, PRIVATE_NOTICE))
      : site.access_note && h('p', { class: 'notice' }, icon(ICONS.home), h('span', {}, site.access_note)),
    checked
      ? (site.summary || site.story) && h('p', { class: 'summary' }, site.summary || site.story.split(/(?<=\.)\s/)[0])
      : h('p', { class: 'pending' }, 'History coming soon. This site is being fact-checked.'),
    h('div', { class: 'actions' },
      action('Walk', ICONS.walk, () => ctx.onDirections('walk')),
      action('Drive', ICONS.drive, () => ctx.onDirections('drive')),
      action('Street View', ICONS.streetView, null, { href: streetViewUrl(site), target: '_blank', rel: 'noopener noreferrer' }),
      story && action('Read more', ICONS.book, (e) => {
        story.hidden = !story.hidden;
        e.currentTarget.setAttribute('aria-expanded', String(!story.hidden));
        if (!story.hidden) ctx.onExpand?.();
      }, { 'aria-expanded': 'false' }),
      sources.length > 0 && action('Sources', ICONS.quote, (e) => {
        sourcesList.hidden = !sourcesList.hidden;
        e.currentTarget.setAttribute('aria-expanded', String(!sourcesList.hidden));
        if (!sourcesList.hidden) ctx.onExpand?.();
      }, { 'aria-expanded': 'false' })),
    walkNote,
    story,
    sourcesList,
    h('p', { class: 'card-foot' },
      h('button', { type: 'button', class: 'link', onclick: ctx.onChangeMapsApp }, 'Change maps app')));
  return card;
}
